export class AudioProcessor {
  private audioContext: AudioContext | null = null;
  private sourceNode: AudioBufferSourceNode | null = null;
  private mediaStreamDestination: MediaStreamAudioDestinationNode | null = null;
  private opusRecorder: MediaRecorder | null = null;
  private onOpusData: ((data: Blob) => void) | null = null;
  private isPlaying: boolean = false;
  private recorderError: Error | null = null;
  private onError: ((error: Error) => void) | null = null;

  async initialize(sampleRate: number = 16000): Promise<void> {
    if (this.audioContext) {
      await this.audioContext.close();
    }
    this.audioContext = new AudioContext({ sampleRate });
    this.mediaStreamDestination = this.audioContext.createMediaStreamDestination();
  }

  setOnError(callback: (error: Error) => void): void {
    this.onError = callback;
  }

  async processAudioFile(file: File, onOpusData: (data: Blob) => void): Promise<number> {
    if (!this.audioContext || !this.mediaStreamDestination) {
      await this.initialize();
    }

    this.onOpusData = onOpusData;

    // Decode audio file
    let arrayBuffer: ArrayBuffer;
    try {
      arrayBuffer = await file.arrayBuffer();
    } catch (error) {
      throw new Error(`Failed to read file: ${error instanceof Error ? error.message : String(error)}`);
    }

    let audioBuffer: AudioBuffer;
    try {
      audioBuffer = await this.audioContext!.decodeAudioData(arrayBuffer);
    } catch (error) {
      throw new Error(`Failed to decode audio file: ${error instanceof Error ? error.message : String(error)}\nFile may be corrupted or in unsupported format.`);
    }
    
    const duration = audioBuffer.duration;
    
    if (duration === 0 || !isFinite(duration)) {
      throw new Error('Invalid audio file: duration is 0 or invalid');
    }

    // Try to create MediaRecorder with Opus codec
    let mimeType = 'audio/webm;codecs=opus';
    const supportedTypes: string[] = [];
    
    // Check what codecs are supported
    const testTypes = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/ogg;codecs=opus',
      'audio/mp4',
    ];
    
    for (const type of testTypes) {
      if (MediaRecorder.isTypeSupported(type)) {
        supportedTypes.push(type);
        if (!mimeType || mimeType === '') {
          mimeType = type;
        }
      }
    }
    
    console.log('Supported MediaRecorder types:', supportedTypes);
    console.log('Using mimeType:', mimeType || 'default');

    const options: MediaRecorderOptions = mimeType
      ? {
          mimeType,
          audioBitsPerSecond: 48000,
        }
      : {
          audioBitsPerSecond: 48000,
        };

    try {
      this.opusRecorder = new MediaRecorder(this.mediaStreamDestination!.stream, options);
      console.log('MediaRecorder created successfully with mimeType:', this.opusRecorder.mimeType);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      console.error('Failed to create MediaRecorder:', errorMsg, 'Options:', options);
      throw new Error(`Failed to create MediaRecorder: ${errorMsg}\nBrowser may not support the selected codec.\nSupported types: ${supportedTypes.join(', ') || 'none'}`);
    }

    // Setup recorder event handlers
    this.opusRecorder.ondataavailable = (event) => {
      if (event.data.size > 0 && this.onOpusData && this.isPlaying) {
        try {
          this.onOpusData(event.data);
        } catch (error) {
          console.error('Error in onOpusData callback:', error);
        }
      }
    };

    this.opusRecorder.onerror = (event) => {
      const error = (event as any).error;
      const errorMessage = error 
        ? `MediaRecorder error: ${error.name} - ${error.message}`
        : `MediaRecorder error: ${event.type}`;
      console.error(errorMessage);
      this.recorderError = new Error(errorMessage);
      if (this.onError) {
        this.onError(this.recorderError);
      }
    };

    // Create source node
    this.sourceNode = this.audioContext!.createBufferSource();
    this.sourceNode.buffer = audioBuffer;
    this.sourceNode.connect(this.mediaStreamDestination!);

    // Handle end of playback
    this.sourceNode.onended = () => {
      this.stopRecording();
    };

    return duration;
  }

  startRecording(): void {
    // Check for previous errors
    if (this.recorderError) {
      throw this.recorderError;
    }

    if (!this.opusRecorder) {
      throw new Error('MediaRecorder not initialized. Call processAudioFile first.');
    }
    if (!this.sourceNode) {
      throw new Error('Audio source node not initialized. Call processAudioFile first.');
    }

    this.isPlaying = true;
    this.recorderError = null; // Reset error
    
    console.log('Starting recording, MediaRecorder state:', this.opusRecorder.state);
    
    let recorderStarted = false;
    try {
      if (this.opusRecorder.state === 'inactive') {
        this.opusRecorder.start(20); // 20ms chunks
        recorderStarted = true;
        console.log('MediaRecorder started, new state:', this.opusRecorder.state);
      } else if (this.opusRecorder.state === 'recording') {
        console.log('MediaRecorder already recording');
        recorderStarted = true;
        return;
      } else {
        console.warn('MediaRecorder in unexpected state:', this.opusRecorder.state);
      }
    } catch (error) {
      const err = new Error(`Failed to start MediaRecorder: ${error instanceof Error ? error.message : String(error)}`);
      console.error('Error starting MediaRecorder:', err);
      this.recorderError = err;
      this.isPlaying = false;
      throw err;
    }

    try {
      this.sourceNode.start(0);
      console.log('Audio source node started');
    } catch (error) {
      const err = new Error(`Failed to start audio playback: ${error instanceof Error ? error.message : String(error)}`);
      console.error('Error starting audio source:', err);
      this.recorderError = err;
      this.isPlaying = false;
      // Stop MediaRecorder if it was started but source failed
      if (recorderStarted && this.opusRecorder) {
        try {
          // MediaRecorder was started, try to stop it
          if (this.opusRecorder.state !== 'inactive') {
            this.opusRecorder.stop();
          }
        } catch (e) {
          // Ignore stop errors
          console.warn('Error stopping MediaRecorder:', e);
        }
      }
      throw err;
    }
  }

  stopRecording(): void {
    this.isPlaying = false;
    if (this.opusRecorder && this.opusRecorder.state === 'recording') {
      this.opusRecorder.stop();
    }
    if (this.sourceNode) {
      try {
        this.sourceNode.stop();
      } catch (e) {
        // Already stopped or not started
      }
    }
  }

  cleanup(): void {
    this.stopRecording();
    if (this.sourceNode) {
      try {
        this.sourceNode.disconnect();
      } catch (e) {
        // Already disconnected
      }
      this.sourceNode = null;
    }
    if (this.opusRecorder) {
      this.opusRecorder = null;
    }
    if (this.mediaStreamDestination) {
      try {
        this.mediaStreamDestination.disconnect();
      } catch (e) {
        // Already disconnected
      }
      this.mediaStreamDestination = null;
    }
    if (this.audioContext) {
      this.audioContext.close().catch(() => {
        // Ignore errors
      });
      this.audioContext = null;
    }
  }
}

